import React, { FunctionComponent, useState } from 'react';
import Button from '$/components/ui/Button/index';
import { ReactComponent as AddIcon } from '$/images/add.svg';
import { ReactComponent as ArrowDownIcon } from '$/images/arrowDown.svg';
import {
  UserRole,
  UserOrderByFields,
  OrderDirection,
  useTeamUsersQuery,
  MemberFragment,
  useResendInvitationEmailMutation,
  useUpdateUserMutation,
  useMeQuery,
  EditUserInput,
  MyRole,
} from '$/GraphQL/schema/schema';
import Loading from '$/components/Loading';
import TeamMember from '$/sections/Home/Profile/TeamManager/TeamMember';
import { StatusMessage } from '$/components/ui/MessageText';
import RemoveMemberModal from './RemoveMemberModal';
import { css } from '@emotion/react';
import UserEditModal from './UserEditModal';
import { TEAM_USERS_REFETCH } from '$/GraphQL/refetchQueries';
import SEO from '$/components/SEO';
import { GraphQLError } from 'graphql';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { Popup } from '$/components/ui/Popup';
import Text from '$/components/ui/Text';
import Settings from '$/components/ui/Settings';

export type TeamFormModes = 'create' | 'edit';

export const AdminRoles = [MyRole.Ops, MyRole.Admin];

export interface DropdownOption {
  value: string;
  text: string;
}

export const dropdownRole = [
  { value: MyRole.Admin, text: MyRole.Admin },
  { value: MyRole.Creator, text: MyRole.Creator },
  { value: MyRole.Editor, text: MyRole.Editor },
  { value: MyRole.Moderator, text: MyRole.Moderator },
  { value: MyRole.Presenter, text: MyRole.Presenter },
];

export const RolePopup: React.FC = () => {
  return (
    <Popup
      content={
        <table
          css={css`
            border: 0;
            td {
              vertical-align: top;
            }
            td:first-of-type {
              text-align: right;
              padding-right: 0.5rem;
              text-align:;
            }
          `}
        >
          <tbody>
            <tr>
              <td>Admin</td>
              <td>Can manage eWebinars, settings & billing</td>
            </tr>
            <tr>
              <td>Creator</td>
              <td>Able to create and edit eWebinars</td>
            </tr>
            <tr>
              <td>Editor</td>
              <td>Able to edit eWebinars they're assigned to</td>
            </tr>
            <tr>
              <td>Moderator</td>
              <td>Can chat with attendees of eWebinars they're assigned to</td>
            </tr>
            <tr>
              <td>Presenter</td>
              <td>Used only to display presenter info during eWebinar</td>
            </tr>
          </tbody>
        </table>
      }
      key='Role'
    />
  );
};

export const CreatorUserDefaults: Partial<EditUserInput> = {
  firstName: '',
  lastName: '',
  email: '',
  role: UserRole.Creator,
  profileMediaRef: null,
  company: '',
  title: '',
  bio: '',
  phone: '',
  timezone: LocalTimeZone.name,
  socialLinks: {
    facebook: '',
    twitter: '',
    linkedin: '',
    instagram: '',
  },
};

const TeamManager: FunctionComponent = () => {
  const [defaultValues, setDefaultValues] = useState<Partial<EditUserInput>>(CreatorUserDefaults);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [mode, setMode] = useState<TeamFormModes>('create');
  const [errors, setErrors] = React.useState<GraphQLError[] | undefined>();
  const [message, setMessage] = React.useState<string | undefined>();
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [member, setMember] = useState<MemberFragment>();
  const [isAscDirection, setIsAscDirection] = useState(true);

  const [updateUserInTeam, updateUserInTeamResult] = useUpdateUserMutation();
  const [resendInvitationEmail, { loading }] = useResendInvitationEmailMutation();

  const meQuery = useMeQuery();

  const teamUsersQuery = useTeamUsersQuery({
    variables: {
      filters: {
        orderBy: UserOrderByFields.FirstName,
        orderDirection: isAscDirection ? OrderDirection.Asc : OrderDirection.Desc,
      },
    },
  });

  if (!meQuery.data || !meQuery.data.me) {
    return <Loading query={meQuery} />;
  }
  const me = meQuery.data.me;

  if (teamUsersQuery.error) {
    return <Loading query={teamUsersQuery} />;
  }

  const teamUsers = (teamUsersQuery.data?.teamUsers ?? []).filter(
    (u) => u.role !== UserRole.Presenter
  );
  const presenterUsers = (teamUsersQuery.data?.teamUsers ?? []).filter(
    (u) => u.role === UserRole.Presenter
  );

  const changedRoleSuccess = 'Changes saved';
  const sentEmailSuccess = 'Invitation email sent.';

  const openCreateUser = (): void => {
    setModalIsOpen(true);
  };

  const openCreatePresenter = (): void => {
    setDefaultValues({ ...CreatorUserDefaults, role: UserRole.Presenter });
    setModalIsOpen(true);
  };

  const openEditMember = (member: MemberFragment): void => {
    setModalIsOpen(true);

    setDefaultValues(member as EditUserInput);
    setMode('edit');
  };

  const closeModal = (): void => {
    setModalIsOpen(false);
    setDefaultValues(CreatorUserDefaults);
    setMode('create');
  };

  const handleResendEmail = async (userId: string, teamId: string): Promise<void> => {
    setErrors(undefined);
    setMessage(undefined);
    try {
      const result = await resendInvitationEmail({
        variables: { userId, teamId },
      });
      if (result && result.errors) {
        setErrors(result.errors);
      }
      if (result && result.data && result.data.resendInvitationEmail) {
        setMessage(sentEmailSuccess);
      }
    } catch (e) {
      const error = e as GraphQLError;

      console.error('Error ', { error });
      setErrors([error]);
    }
  };

  const handleChangeRole = async (member: MemberFragment, role: UserRole): Promise<void> => {
    setErrors(undefined);
    setMessage(undefined);

    let error: GraphQLError | null = null;
    let result;

    try {
      result = await updateUserInTeam({
        variables: {
          data: {
            id: member.id,
            role: role,
            firstName: member.firstName,
            lastName: member.lastName ? member.lastName : '',
            email: member.email,
          },
        },
        refetchQueries: [TEAM_USERS_REFETCH],
      });
    } catch (e) {
      error = e as GraphQLError;
    }

    if (error) {
      setErrors([error]);
    }

    if (result && result.errors) {
      setErrors(result.errors);
    }
    if (result && result.data && result.data.updateUser) {
      setMessage(changedRoleSuccess);
    }
  };

  const handleRemoveMember = async (member: MemberFragment): Promise<void> => {
    setErrors(undefined);
    setMessage(undefined);

    setMember(member);
    setShowRemoveModal(true);
  };

  const handleCloseRemoveModal = (): void => {
    setShowRemoveModal(false);
  };

  return (
    <Settings.Page hasSections>
      <SEO title='My Team' />
      {modalIsOpen && (
        <UserEditModal mode={mode} defaultValues={defaultValues} onClose={closeModal} />
      )}

      {showRemoveModal && member && (
        <RemoveMemberModal
          me={me}
          member={member}
          users={teamUsers}
          onClose={handleCloseRemoveModal}
        />
      )}

      <Settings.Section id='integrations'>
        <Settings.Subsection className='pb-0 flex'>
          <div className='flex w-full justify-between'>
            <Settings.SectionTitle>My team</Settings.SectionTitle>
            <Button
              id='btnAddNewMember'
              className='flex pt-1/4e ml-3 items-center pl-1e text-blue-3'
              onClick={(): void => openCreateUser()}
            >
              <AddIcon className='h-1e mr-2' />
              New team member
            </Button>
          </div>
          <div className='flex w-full justify-between items-center'>
            <Settings.Description className='flex flex-1 w-full'>
              Learn about different team roles and permissions{' '}
              <a
                className='ml-1/4e'
                href='https://ewebinar.com/help/what-are-the-ewebinar-team-roles-and-what-are-they-allowed-to-do'
                target='_blank'
              >
                here
              </a>
              .
            </Settings.Description>
            <div className='flex'>
              <StatusMessage
                className='flex mt-0 justify-end'
                errors={errors}
                message={message}
                hideCircles
              />
            </div>
          </div>

          <div className='py-5'>
            <div className='flex justify-between py-2'>
              <div className={`w-3/12 flex content-center items-center`}>
                <div
                  className='flex items-center cursor-pointer'
                  onClick={(): void => {
                    setIsAscDirection(!isAscDirection);
                  }}
                >
                  <Text className='text-gray-2 text-sm mr-1'>Name</Text>
                  <ArrowDownIcon
                    className='ml-1 text-color-arrowDown'
                    css={css`
                      ${isAscDirection && 'transform: rotate(-180deg);'}
                    `}
                  />
                </div>
              </div>
              <div className='w-3/12 flex'>
                <Text className='text-gray-2 text-sm pl-2'>Email</Text>
              </div>
              <div className='w-2/12 flex'>
                <Text className='text-gray-2 text-sm pl-2'>Role</Text>
                <RolePopup />
              </div>
              <div className='w-2/12 flex'>
                <Text className={`text-gray-2 text-sm pl-2`}>Status</Text>
              </div>
              <div className='w-2/12 flex' />
            </div>

            {teamUsersQuery.data?.teamUsers && (
              <>
                {teamUsers.map((member: MemberFragment) => (
                  <TeamMember
                    key={member.id}
                    member={member}
                    changeRoleUser={handleChangeRole}
                    openEditMember={openEditMember}
                    resendInvitationEmail={handleResendEmail}
                    removeMember={handleRemoveMember}
                    dropdownRole={dropdownRole}
                    isLoading={loading}
                    me={me}
                    roleChangedLoading={updateUserInTeamResult.loading}
                  />
                ))}

                <div className='flex w-full items-center justify-between mt-2e'>
                  <Settings.Title>Presenters</Settings.Title>
                  <Button
                    id='btnAddNewMember'
                    className='flex pt-1/4e ml-3 items-center pl-4 text-blue-3'
                    onClick={(): void => openCreatePresenter()}
                  >
                    <AddIcon className='h-1e mr-2' />
                    New presenter
                  </Button>
                </div>

                <Settings.Description>
                  Presenters make it easy to describe the people in your webinar videos who aren't
                  part of your team.
                </Settings.Description>
                <Settings.Separator className='mt-3/4e mb-1/4e py-0' />

                {presenterUsers.map((member: MemberFragment) => (
                  <TeamMember
                    key={member.id}
                    member={member}
                    changeRoleUser={handleChangeRole}
                    openEditMember={openEditMember}
                    resendInvitationEmail={handleResendEmail}
                    removeMember={handleRemoveMember}
                    dropdownRole={dropdownRole}
                    isLoading={loading}
                    me={me}
                    roleChangedLoading={updateUserInTeamResult.loading}
                  />
                ))}
              </>
            )}
          </div>
        </Settings.Subsection>
      </Settings.Section>
    </Settings.Page>
  );
};

export default TeamManager;
